import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { switchMap } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { FormRuntimeRendererComponent } from '../../shared/dynamic-form/form-runtime-renderer.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService } from '../dynamic/dynamic-api.service';
import { OrganizationDynamicApiService } from '../workflow/organization-dynamic-api.service';
import {
  GROWTH_BUSINESS_DISCOVERY_FORM_KEY,
  GrowthBusinessDiscoveryDetail,
  GrowthJourneyService
} from './growth-journey.service';
import { GrowthDiscoverySwotPanelComponent } from './growth-discovery-swot-panel.component';
import { GrowthDiscoverySituationAssessmentPanelComponent } from './growth-discovery-situation-assessment-panel.component';
import { GrowthMarketCompetitorPanelComponent } from './growth-market-competitor-panel.component';
import { GrowthDiscoveryPositioningPanelComponent } from './growth-discovery-positioning-panel.component';
import { GrowthDiscoveryMarketingPlanPanelComponent } from './growth-discovery-marketing-plan-panel.component';
import {
  DISCOVERY_STATUS_I18N_KEYS,
  discoveryStatusSeverity as resolveDiscoveryStatusSeverity,
  normalizeDiscoveryStatus
} from './growth-status.util';

interface BusinessProfileSnapshot {
  name?: string;
  industry?: string;
  description?: string;
  targetCustomer?: string;
  valueProposition?: string;
  geographicScope?: string;
}

interface PublicFormSubmissionResponse {
  submissionId?: string;
  SubmissionId?: string;
  error?: string | null;
  Error?: string | null;
}

@Component({
  standalone: true,
  selector: 'ain-growth-business-discovery-detail',
  providers: [MessageService],
  imports: [
    FormsModule,
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    SelectModule,
    FormRuntimeRendererComponent,
    ButtonModule,
    InputTextModule,
    TagModule,
    TableModule,
    GrowthMarketCompetitorPanelComponent,
    GrowthDiscoverySwotPanelComponent,
    GrowthDiscoverySituationAssessmentPanelComponent,
    GrowthDiscoveryPositioningPanelComponent,
    GrowthDiscoveryMarketingPlanPanelComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="pageTitle()"
      [description]="t('growth.journey.discovery.detail.description')">
      <div class="toolbar">
        <a pButton routerLink="/growth/journey/discoveries" severity="secondary" [label]="t('common.back')"></a>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.discovery.help.title'"
        [descriptionKey]="'growth.journey.discovery.help.description'"
        [bulletKeys]="['growth.journey.discovery.help.bullets.swotInput']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else {
        <section class="card">
          @if (discovery(); as item) {
            <div class="meta-row">
              <p-tag [value]="discoveryStatusLabel(item.status)" [severity]="discoveryStatusSeverity(item.status)" />
            </div>
          }

          <div class="grid">
            <ain-form-field [label]="t('growth.journey.discovery.fields.title')">
              <input pInputText class="text-input" [(ngModel)]="model.title" [disabled]="isArchived()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.discovery.fields.locale')">
              <p-select
                [options]="localeOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.locale"
                [disabled]="isArchived()"
                appendTo="body" />
            </ain-form-field>
          </div>

          @if (isNew()) {
            <div class="section-title">
              <h3>{{ t('growth.journey.discovery.sections.intakeForm') }}</h3>
            </div>
            @if (formSchemaLoading()) {
              <ain-state type="loading" [title]="t('common.loading')" />
            } @else if (formSchemaError()) {
              <ain-state type="error" [title]="formSchemaError() ?? t('common.loadFailed')" />
            } @else {
              <ain-form-runtime-renderer
                #formRenderer
                [schemaJson]="formSchemaSnapshot()"
                mode="runtime"
                [lookupSearchEnabled]="true"
                [showSubmitFooter]="false"
                [initialValues]="{}" />
            }
          } @else if (discovery()) {
            <div class="section-title">
              <h3>{{ t('growth.journey.discovery.sections.snapshotPreview') }}</h3>
            </div>
            @if (businessProfile(); as profile) {
              <div class="preview-block">
                <h4>{{ t('growth.journey.discovery.sections.businessProfile') }}</h4>
                <dl class="preview-grid">
                  <dt>{{ t('growth.journey.discovery.snapshot.name') }}</dt><dd>{{ displayValue(profile.name) }}</dd>
                  <dt>{{ t('growth.journey.discovery.snapshot.industry') }}</dt><dd>{{ displayValue(profile.industry) }}</dd>
                  <dt>{{ t('growth.journey.discovery.snapshot.targetCustomer') }}</dt><dd>{{ displayValue(profile.targetCustomer) }}</dd>
                  <dt>{{ t('growth.journey.discovery.snapshot.valueProposition') }}</dt><dd>{{ displayValue(profile.valueProposition) }}</dd>
                  <dt>{{ t('growth.journey.discovery.snapshot.geographicScope') }}</dt><dd>{{ displayValue(profile.geographicScope) }}</dd>
                </dl>
                @if (profile.description) {
                  <p class="preview-text">{{ profile.description }}</p>
                }
              </div>
            }
          }

          @if (!isNew() && discovery(); as item) {
            <ain-growth-market-competitor-panel
              [discoveryId]="item.id"
              [discoveryStatus]="item.status"
              (discoveryChanged)="reloadDiscovery()" />
            <ain-growth-discovery-swot-panel
              [discoveryId]="item.id"
              [discoveryStatus]="item.status"
              (discoveryChanged)="reloadDiscovery()" />
            <ain-growth-discovery-situation-assessment-panel
              [discoveryId]="item.id"
              [latestSituationAssessmentArtifactId]="item.latestSituationAssessmentArtifactId" />
            <ain-growth-discovery-positioning-panel
              [discoveryId]="item.id"
              [latestPositioningArtifactId]="item.latestPositioningArtifactId"
              (discoveryChanged)="reloadDiscovery()" />
            <ain-growth-discovery-marketing-plan-panel
              [discoveryId]="item.id"
              [latestPositioningArtifactId]="item.latestPositioningArtifactId"
              [latestMarketingPlanArtifactId]="item.latestMarketingPlanArtifactId"
              (discoveryChanged)="reloadDiscovery()" />
          }

          @if (actionError()) {
            <p class="action-error">{{ actionError() }}</p>
          }

          <div class="actions">
            @if (isNew()) {
              <button
                pButton
                type="button"
                [label]="t('common.create')"
                [loading]="saving()"
                [disabled]="formSchemaLoading() || !!formSchemaError()"
                (click)="createFromForm()"></button>
            } @else if (discovery()) {
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('common.save')"
                [loading]="saving()"
                [disabled]="isArchived()"
                (click)="saveMetadata()"></button>
              @if (canMarkReady()) {
                <button
                  pButton
                  type="button"
                  [label]="t('growth.journey.discovery.markReady')"
                  [loading]="markingReady()"
                  (click)="markReady()"></button>
              }
              @if (canStartGuidedJourney()) {
                <button
                  pButton
                  type="button"
                  severity="help"
                  [label]="t('growth.journey.guided.actions.startJourney')"
                  [loading]="startingJourney()"
                  (click)="startGuidedJourney()"></button>
              }
              @if (canViewGuidedJourney()) {
                <a
                  pButton
                  severity="help"
                  [routerLink]="['/growth/journey/journeys', discovery()?.growthJourneyId]"
                  [label]="t('growth.journey.guided.actions.viewJourney')"></a>
              }
              @if (canArchive()) {
                <button
                  pButton
                  type="button"
                  severity="danger"
                  [label]="t('growth.journey.discovery.actions.archive')"
                  [loading]="archiving()"
                  (click)="archive()"></button>
              }
            }
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 12px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
    .section-title h3, .preview-block h4, .preview-block h5 { margin: 0 0 8px; }
    .preview-block { border-top: 1px solid #e5e7eb; padding-top: 12px; display: grid; gap: 8px; }
    .preview-grid { display: grid; gap: 6px 12px; grid-template-columns: minmax(140px, 220px) 1fr; margin: 0; }
    .preview-grid dt { margin: 0; color: #6b7280; font-weight: 600; }
    .preview-grid dd { margin: 0; }
    .preview-text { margin: 0; color: #374151; white-space: pre-wrap; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .text-input { width: 100%; }
    .action-error { margin: 0; color: #b91c1c; }
  `]
})
export class GrowthBusinessDiscoveryDetailComponent implements OnInit {
  @ViewChild('formRenderer') private formRenderer?: FormRuntimeRendererComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly journey = inject(GrowthJourneyService);
  private readonly dynamicApi = inject(DynamicApiService);
  private readonly organizationDynamicApi = inject(OrganizationDynamicApiService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isNew = signal(false);
  readonly discovery = signal<GrowthBusinessDiscoveryDetail | null>(null);
  readonly formSchemaLoading = signal(false);
  readonly formSchemaError = signal<string | null>(null);
  readonly formSchemaSnapshot = signal('{}');
  readonly saving = signal(false);
  readonly markingReady = signal(false);
  readonly archiving = signal(false);
  readonly startingJourney = signal(false);
  readonly actionError = signal<string | null>(null);
  readonly businessProfile = signal<BusinessProfileSnapshot | null>(null);

  model = {
    title: '',
    locale: 'fa-IR'
  };

  get localeOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'fa-IR', label: this.t('growth.journey.discovery.locale.faIR') },
      { value: 'en-US', label: this.t('growth.journey.discovery.locale.enUS') }
    ];
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const discoveryId = params.get('discoveryId');
      this.isNew.set(!discoveryId || discoveryId === 'new');
      this.actionError.set(null);

      if (this.isNew()) {
        this.discovery.set(null);
        this.loading.set(false);
        this.loadFormSchema();
        return;
      }

      this.loadDiscovery(discoveryId!);
    });
  }

  pageTitle(): string {
    return this.isNew()
      ? this.t('growth.journey.discovery.actions.new')
      : (this.discovery()?.title ?? this.t('growth.journey.discovery.title'));
  }

  discoveryStatusLabel(status: unknown): string {
    const normalized = normalizeDiscoveryStatus(status);
    return this.t(DISCOVERY_STATUS_I18N_KEYS[normalized]);
  }

  discoveryStatusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveDiscoveryStatusSeverity(status);
  }

  isArchived(): boolean {
    return normalizeDiscoveryStatus(this.discovery()?.status) === 'Archived';
  }

  canMarkReady(): boolean {
    const status = normalizeDiscoveryStatus(this.discovery()?.status);
    return status !== 'Archived' && status !== 'ReadyForSwot' && status !== 'LinkedToJourney';
  }

  canArchive(): boolean {
    return normalizeDiscoveryStatus(this.discovery()?.status) !== 'Archived';
  }

  canStartGuidedJourney(): boolean {
    const item = this.discovery();
    if (!item) {
      return false;
    }

    return normalizeDiscoveryStatus(item.status) === 'ReadyForSwot' && !item.growthJourneyId;
  }

  canViewGuidedJourney(): boolean {
    const item = this.discovery();
    if (!item?.growthJourneyId) {
      return false;
    }

    return normalizeDiscoveryStatus(item.status) === 'LinkedToJourney';
  }

  startGuidedJourney(): void {
    const current = this.discovery();
    if (!current || !this.canStartGuidedJourney()) {
      return;
    }

    this.startingJourney.set(true);
    this.actionError.set(null);
    this.journey.startJourney(current.id).subscribe({
      next: created => {
        this.startingJourney.set(false);
        this.router.navigate(['/growth/journey/journeys', created.id]);
      },
      error: err => {
        this.startingJourney.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return String(value);
  }

  createFromForm(): void {
    const renderer = this.formRenderer;
    if (!renderer) {
      return;
    }

    const validationIssues = renderer.validate();
    if (validationIssues.length > 0) {
      this.actionError.set(this.t('growth.journey.discovery.formValidation'));
      return;
    }

    this.saving.set(true);
    this.actionError.set(null);
    const payload = renderer.getCurrentValues();

    this.organizationDynamicApi.submitPublicForm(GROWTH_BUSINESS_DISCOVERY_FORM_KEY, payload).pipe(
      switchMap(response => {
        const submissionId = this.extractSubmissionId(response);
        if (!submissionId) {
          throw new Error('growth_discovery_submission_failed');
        }

        return this.journey.createDiscovery({
          dynamicFormSubmissionId: submissionId,
          title: this.model.title.trim() || null,
          locale: this.model.locale
        });
      })
    ).subscribe({
      next: created => {
        this.saving.set(false);
        this.router.navigate(['/growth/journey/discoveries', created.id]);
      },
      error: err => {
        this.saving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  saveMetadata(): void {
    const current = this.discovery();
    if (!current || !this.model.title.trim()) {
      return;
    }

    this.saving.set(true);
    this.actionError.set(null);
    this.journey.updateDiscovery(current.id, {
      title: this.model.title.trim(),
      locale: this.model.locale
    }).subscribe({
      next: updated => {
        this.discovery.set(updated);
        this.hydrateSnapshots(updated);
        this.saving.set(false);
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
      },
      error: err => {
        this.saving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  markReady(): void {
    const current = this.discovery();
    if (!current) {
      return;
    }

    this.markingReady.set(true);
    this.actionError.set(null);
    this.journey.markReadyForSwot(current.id).subscribe({
      next: updated => {
        this.discovery.set(updated);
        this.hydrateSnapshots(updated);
        this.markingReady.set(false);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.discovery.markReady') });
      },
      error: err => {
        this.markingReady.set(false);
        const message = this.resolveActionError(err);
        this.actionError.set(message);
        this.messages.add({ severity: 'warn', summary: message });
      }
    });
  }

  reloadDiscovery(): void {
    const current = this.discovery();
    if (!current) {
      return;
    }

    this.journey.getDiscovery(current.id).subscribe({
      next: updated => {
        this.discovery.set(updated);
        this.hydrateSnapshots(updated);
      }
    });
  }

  archive(): void {
    const current = this.discovery();
    if (!current) {
      return;
    }

    this.archiving.set(true);
    this.actionError.set(null);
    this.journey.archiveDiscovery(current.id).subscribe({
      next: updated => {
        this.discovery.set(updated);
        this.hydrateSnapshots(updated);
        this.archiving.set(false);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.discovery.actions.archived') });
      },
      error: err => {
        this.archiving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadDiscovery(id: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.journey.getDiscovery(id).subscribe({
      next: item => {
        this.discovery.set(item);
        this.model = {
          title: item.title,
          locale: item.locale
        };
        this.hydrateSnapshots(item);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.notFound'));
        this.loading.set(false);
      }
    });
  }

  private loadFormSchema(): void {
    this.formSchemaLoading.set(true);
    this.formSchemaError.set(null);

    this.dynamicApi.listForms().pipe(
      switchMap(forms => {
        const form = forms.find(item => item.name === GROWTH_BUSINESS_DISCOVERY_FORM_KEY);
        if (!form) {
          throw new Error('growth_discovery_form_not_found');
        }

        return this.dynamicApi.getFormRuntime(form.id);
      })
    ).subscribe({
      next: runtime => {
        this.formSchemaSnapshot.set(runtime.schemaSnapshot || '{}');
        this.formSchemaLoading.set(false);
      },
      error: () => {
        this.formSchemaError.set(this.t('growth.journey.discovery.formNotFound'));
        this.formSchemaLoading.set(false);
      }
    });
  }

  private hydrateSnapshots(item: GrowthBusinessDiscoveryDetail): void {
    this.businessProfile.set(this.parseJson<BusinessProfileSnapshot>(item.businessProfileJson));
  }

  private parseJson<T>(json: string): T | null {
    try {
      return JSON.parse(json) as T;
    } catch {
      return null;
    }
  }

  private extractSubmissionId(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const payload = response as PublicFormSubmissionResponse;
    return payload.submissionId ?? payload.SubmissionId ?? null;
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_discovery_incomplete') {
      return this.t('growth.journey.discovery.incomplete');
    }

    if (code === 'growth_discovery_form_not_found') {
      return this.t('growth.journey.discovery.formNotFound');
    }

    if (code === 'growth_discovery_submission_failed') {
      return this.t('growth.journey.discovery.submissionFailed');
    }

    if (code === 'growth_guided_journey_discovery_not_ready') {
      return this.t('growth.journey.guided.errors.discoveryNotReady');
    }

    if (code === 'growth_guided_journey_already_exists') {
      return this.t('growth.journey.guided.errors.alreadyExists');
    }

    return code ?? this.t('common.error');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      if (error instanceof Error) {
        return error.message;
      }

      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    if (typeof body['Message'] === 'string') {
      return body['Message'];
    }

    return null;
  }
}
